import type { Portfolio } from 'venn-api';
import { sum } from 'lodash';

type TaxImpactRowType = 'TRANSACTION' | 'UNEXECUTED' | 'UNFILLED' | 'MISSING' | 'TOTAL';

export type TaxImpactRow = {
  label: string | undefined;
  path: string[];
  isStrategy: boolean;
  purchaseDate: string | undefined;
  holdingPeriod: string | undefined;
  marketValue: number | undefined;
  costBasis: number | undefined;
  startingQuantity: number | undefined;
  transactedQuantity: number | undefined;
  endingQuantity: number | undefined;
  gainLoss: number | undefined;
  estimatedTax: number | undefined;
  taxStatus: string | undefined;
  rowType: TaxImpactRowType;
};

export type ProcessedLot = Omit<TaxImpactRow, 'path' | 'isStrategy'> & {
  investmentId: string;
};

const rollUpNumericValues = (rows: (TaxImpactRow | ProcessedLot)[]) => ({
  marketValue: sum(rows.map((r) => r.marketValue ?? 0)),
  costBasis: sum(rows.map((r) => r.costBasis ?? 0)),
  startingQuantity: sum(rows.map((r) => r.startingQuantity)),
  transactedQuantity: sum(rows.map((r) => r.transactedQuantity)),
  endingQuantity: sum(rows.map((r) => r.endingQuantity)),
  gainLoss: sum(rows.map((r) => r.gainLoss)),
  estimatedTax: sum(rows.map((r) => r.estimatedTax)),
});

const buildInvestmentRows = (
  name: string | undefined,
  parentPath: string[],
  lots: ProcessedLot[],
  nameIndex: number,
): TaxImpactRow[] => {
  const displayName = name ?? '';
  const investmentPath = [...parentPath, `${displayName}(${nameIndex})`];

  if (lots.length <= 1) {
    const lot = lots[0]!;
    return [{ ...lot, path: investmentPath, label: displayName, isStrategy: false }];
  }

  return [
    {
      path: investmentPath,
      label: displayName,
      rowType: 'TOTAL',
      isStrategy: false,
      purchaseDate: undefined,
      holdingPeriod: undefined,
      taxStatus: undefined,
      ...rollUpNumericValues(lots),
    },
    ...lots.map(
      (lot, idx): TaxImpactRow => ({
        ...lot,
        path: [...investmentPath, `lot-${idx}`],
        label: displayName,
        isStrategy: false,
      }),
    ),
  ];
};

const collectLotsUnderStrategy = (
  children: Portfolio[],
  lotsByInvestment: Record<string, ProcessedLot[]>,
): ProcessedLot[] => {
  const lots: ProcessedLot[] = [];
  for (const child of children) {
    if (child.fund) {
      const fundLots = lotsByInvestment[child.fund.id];
      if (fundLots?.length) {
        lots.push(...fundLots);
      }
    } else {
      lots.push(...collectLotsUnderStrategy(child.children ?? [], lotsByInvestment));
    }
  }
  return lots;
};

export const buildTaxImpactTreeRows = ({
  portfolioChildren,
  parentPath,
  depth,
  lotsByInvestment,
  selectedLevels,
  placedInvestmentIds,
  usedNames,
}: {
  portfolioChildren: Portfolio[];
  parentPath: string[];
  depth: number;
  lotsByInvestment: Record<string, ProcessedLot[]>;
  selectedLevels: (number | string)[];
  placedInvestmentIds: Set<string>;
  usedNames: Record<string, number>;
}): TaxImpactRow[] => {
  const rows: TaxImpactRow[] = [];

  for (const child of portfolioChildren) {
    if (child.fund) {
      const lots = lotsByInvestment[child.fund.id];
      if (!lots?.length) continue;

      placedInvestmentIds.add(child.fund.id);

      if (!selectedLevels.includes('investments')) continue;

      const nameCount = usedNames[child.name] ?? 0;
      usedNames[child.name] = nameCount + 1;
      rows.push(...buildInvestmentRows(child.name, parentPath, lots, nameCount));
    } else {
      const includeLevel = selectedLevels.includes(depth);

      if (includeLevel) {
        const nameCount = usedNames[child.name] ?? 0;
        usedNames[child.name] = nameCount + 1;
        const childPath = [...parentPath, `${child.name}(${nameCount})`];

        const childRows = buildTaxImpactTreeRows({
          portfolioChildren: child.children ?? [],
          parentPath: childPath,
          depth: depth + 1,
          lotsByInvestment,
          selectedLevels,
          placedInvestmentIds,
          usedNames: {},
        });

        const lotsUnderStrategy = collectLotsUnderStrategy(child.children ?? [], lotsByInvestment);
        if (lotsUnderStrategy.length > 0) {
          const directChildRows = childRows.filter((r) => r.path.length === childPath.length + 1);
          rows.push(
            {
              path: childPath,
              label: child.name,
              rowType: 'TOTAL',
              isStrategy: true,
              purchaseDate: undefined,
              holdingPeriod: undefined,
              taxStatus: undefined,
              ...(directChildRows.length > 0
                ? rollUpNumericValues(directChildRows)
                : rollUpNumericValues(lotsUnderStrategy)),
            },
            ...childRows,
          );
        }
      } else {
        rows.push(
          ...buildTaxImpactTreeRows({
            portfolioChildren: child.children ?? [],
            parentPath,
            depth: depth + 1,
            lotsByInvestment,
            selectedLevels,
            placedInvestmentIds,
            usedNames,
          }),
        );
      }
    }
  }

  return rows;
};
